import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, api, downloadFile, API_BASE } from '../lib/api';
import { AppFrame } from '../components/AppNav';
import { AssuranceNotice, Banner, Field, Loading, Modal, StatusPill } from '../components/ui';
import { PdfView, boxStyle } from '../components/PdfView';
import { formatDateTime, shortDigest, statusLabel } from '../lib/format';
import { useSession } from '../state/session';
import { useToast } from '../state/toast';

const TABS = ['Overview', 'Document', 'Audit trail', 'Evidence'] as const;

export function EnvelopeDetail() {
  const { envelopeId } = useParams();
  const { orgId, can } = useSession();
  const { notify } = useToast();
  const [data, setData] = useState<any>(null);
  const [events, setEvents] = useState<any>(null);
  const [verification, setVerification] = useState<any>(null);
  const [tab, setTab] = useState<typeof TABS[number]>('Overview');
  const [dialog, setDialog] = useState<'void' | 'replace' | 'hold' | null>(null);
  const [reason, setReason] = useState('');
  const [authority, setAuthority] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !envelopeId) return;
    setData(await api.get(`/organizations/${orgId}/envelopes/${envelopeId}`));
  }, [orgId, envelopeId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    if (tab === 'Audit trail' && orgId && envelopeId && !events) {
      api.get(`/organizations/${orgId}/envelopes/${envelopeId}/events`)
        .then(setEvents).catch(() => undefined);
    }
    if (tab === 'Evidence' && orgId && envelopeId && !verification) {
      api.get(`/organizations/${orgId}/envelopes/${envelopeId}/verification`)
        .then(setVerification).catch(() => undefined);
    }
  }, [tab, orgId, envelopeId, events, verification]);

  // While a document is finalizing, poll so the UI reflects readiness.
  useEffect(() => {
    if (data?.status !== 'finalizing') return;
    const timer = setInterval(() => { void load(); }, 3000);
    return () => clearInterval(timer);
  }, [data?.status, load]);

  const act = async (path: string, body?: unknown, successMessage?: string) => {
    setBusy(true); setError(null);
    try {
      await api.post(`/organizations/${orgId}/envelopes/${envelopeId}${path}`, body);
      if (successMessage) notify(successMessage, 'ok');
      setDialog(null); setReason(''); setAuthority('');
      setEvents(null); setVerification(null);
      await load();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That action failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!data) {
    return <AppFrame><div className="shell section"><Loading /></div></AppFrame>;
  }

  const active = ['sent', 'in_progress'].includes(data.status);
  const docBase = `${API_BASE}/organizations/${orgId}/envelopes/${envelopeId}/documents`;

  return (
    <AppFrame actions={<Link className="btn btn--small btn--ghost" to="/app">All agreements</Link>}>
      <div className="shell section stack">
        <header className="stack-s">
          <div className="row row--between row--top">
            <div style={{ minWidth: 0 }}>
              <h1 style={{ margin: 0, fontSize: 'clamp(1.25rem, 1.1rem + 1vw, 1.7rem)' }}>
                {data.title}
              </h1>
              <p className="muted small" style={{ margin: '4px 0 0' }}>
                Created {formatDateTime(data.created_at)}
                {data.sent_at && ` · Sent ${formatDateTime(data.sent_at)}`}
              </p>
            </div>
            <StatusPill status={data.status} />
          </div>

          {data.status === 'finalizing' && (
            <Banner tone="warn" title="All parties signed — preparing final document">
              Signing is finished. The final document and its evidence are being
              built and validated. It is not complete until that finishes.
              {data.sealing_substatus !== 'none' && (
                <> Sealing state: <strong>{statusLabel(data.sealing_substatus)}</strong>.</>
              )}
            </Banner>
          )}
          {data.status === 'finalization_failed' && (
            <Banner tone="danger" title="Finalization failed">
              {data.last_error || 'The final document could not be built.'} Signer
              intent that was already recorded is preserved.
              {can('envelopes:send') && (
                <> <button className="btn btn--small" style={{ marginTop: 8 }}
                           disabled={busy}
                           onClick={() => void act('/retry-finalization', undefined,
                                                   'Finalization retried.')}>
                  Retry finalization
                </button></>
              )}
            </Banner>
          )}
          {data.recipients.some((r: any) => r.status === 'delivery_failed') && (
            <Banner tone="danger" title="An invitation could not be delivered">
              One or more recipients did not receive their link. Check the
              address and send a reminder, or withdraw and replace the
              agreement.
            </Banner>
          )}
          {data.dispatch_state === 'retrying' && (
            <Banner tone="warn" title="Still trying to deliver the invitation">
              The agreement was sent, but the delivery service has not accepted
              the invitation yet. It is being retried. Nobody needs to do
              anything unless this persists.
            </Banner>
          )}
          {data.dispatch_state === 'unconfirmed' && (
            <Banner tone="danger" title="Delivery could not be confirmed">
              These invitations were handed to the delivery service, which never
              confirmed them. They may or may not have arrived, so they have not
              been sent again — a second link to someone who already has one is
              worse than a late one.
              {can('envelopes:send') ? (
                <>
                  {' '}Check the delivery service for each address and record
                  what you found. This is the only way the signature request
                  stops being held open.
                  <div className="stack-s" style={{ marginTop: 12 }}>
                    {(data.dispatch_unconfirmed_recipients ?? []).map((person: any) => (
                      <div key={person.id} className="row row--between">
                        <span className="small"><strong>{person.name}</strong>{' '}
                          <span className="muted">{person.email}</span></span>
                        <span className="row row--tight">
                          <button className="btn btn--small" disabled={busy}
                                  onClick={() => void act('/dispatch/resolve',
                                                          { outcome: 'delivered',
                                                            recipient_ids: [person.id] },
                                                          `Recorded as delivered to ${person.name}.`)}>
                            Delivered
                          </button>
                          <button className="btn btn--small btn--ghost" disabled={busy}
                                  onClick={() => void act('/dispatch/resolve',
                                                          { outcome: 'not_delivered',
                                                            recipient_ids: [person.id] },
                                                          `Recorded as never delivered to ${person.name}.`)}>
                            Never arrived
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="small muted" style={{ margin: '10px 0 0' }}>
                    Each recipient is answered separately, because the outcomes
                    can differ. Once everyone has an answer: if any invitation
                    arrived, the signature request is settled; if none did, this
                    returns to a draft, the request is released and the links are
                    revoked, so you can correct the addresses and send again.
                  </p>
                </>
              ) : (
                <> Someone who can send agreements needs to confirm with the
                delivery service whether they arrived.</>
              )}
            </Banner>
          )}
          {data.status === 'voided' && (
            <Banner tone="danger" title="Withdrawn">{data.void_reason}</Banner>
          )}
          {data.replaced_by_envelope_id && (
            <Banner tone="info" title="Replaced">
              This agreement was replaced.{' '}
              <Link to={`/app/envelopes/${data.replaced_by_envelope_id}`}>
                Open the replacement
              </Link>.
            </Banner>
          )}
        </header>

        {error && <Banner tone="danger" title="That action failed">{error}</Banner>}

        <div className="tabs" role="tablist" aria-label="Agreement sections">
          {TABS.map((item) => (
            <button key={item} type="button" role="tab" className="tab"
                    aria-selected={tab === item} onClick={() => setTab(item)}>
              {item}
            </button>
          ))}
        </div>

        {tab === 'Overview' && (
          <div className="stack">
            <div className="card stack">
              <h2 className="card__title">Recipients</h2>
              <div className="list">
                {data.recipients.map((person: any) => (
                  <div className="panel" key={person.id}>
                    <div className="row row--between row--top">
                      <div style={{ minWidth: 0 }}>
                        <strong style={{ fontSize: '.94rem' }}>{person.name}</strong>
                        <div className="small muted truncate">{person.email}</div>
                      </div>
                      <StatusPill status={person.status} />
                    </div>
                    <div className="kv" style={{ marginTop: 8 }}>
                      <div className="kv__k">Role</div>
                      <div className="kv__v">
                        {person.role === 'copy' ? 'Copy only'
                          : `Signer · step ${person.routing_order}`}
                      </div>
                      {person.auth_method && (
                        <>
                          <div className="kv__k">Authentication</div>
                          <div className="kv__v">
                            {{ email_link: 'Personal emailed link',
                               email_otp: 'Email one-time passcode',
                               sms_otp: 'SMS one-time passcode' }[person.auth_method as string]
                              || person.auth_method}
                            {' — '}{person.auth_outcome}
                            {person.authenticated_at && ` at ${formatDateTime(person.authenticated_at)}`}
                          </div>
                        </>
                      )}
                      {person.first_viewed_at && (
                        <>
                          <div className="kv__k">First opened</div>
                          <div className="kv__v">
                            {formatDateTime(person.first_viewed_at)}
                            <span className="muted small"> · activity only, not consent</span>
                          </div>
                        </>
                      )}
                      {person.completed_at && (
                        <>
                          <div className="kv__k">Signed</div>
                          <div className="kv__v">{formatDateTime(person.completed_at)}</div>
                        </>
                      )}
                      {person.declined_at && (
                        <>
                          <div className="kv__k">Declined</div>
                          <div className="kv__v">
                            {formatDateTime(person.declined_at)} — {person.decline_reason}
                          </div>
                        </>
                      )}
                      {person.reminder_count > 0 && (
                        <>
                          <div className="kv__k">Reminders</div>
                          <div className="kv__v">
                            {person.reminder_count} sent
                            {person.last_reminded_at && `, last ${formatDateTime(person.last_reminded_at)}`}
                          </div>
                        </>
                      )}
                    </div>
                    {active && can('envelopes:send') && person.role === 'signer'
                      && person.status !== 'completed' && (
                      <button className="btn btn--small" style={{ marginTop: 10 }}
                              disabled={busy}
                              onClick={() => void act('/remind', { recipient_id: person.id },
                                                      'Reminder sent.')}>
                        Send reminder
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="card stack">
              <h2 className="card__title">Timing</h2>
              <div className="kv">
                <div className="kv__k">Sent</div>
                <div className="kv__v">{formatDateTime(data.sent_at)}</div>
                <div className="kv__k">All signers finished</div>
                <div className="kv__v">{formatDateTime(data.all_signed_at)}</div>
                <div className="kv__k">Final document ready</div>
                <div className="kv__v">{formatDateTime(data.completed_at)}</div>
                <div className="kv__k">Expires</div>
                <div className="kv__v">{formatDateTime(data.expires_at)}</div>
                <div className="kv__k">Retained until</div>
                <div className="kv__v">
                  {formatDateTime(data.retention.retention_until)}
                  {data.retention.on_hold && <span className="pill pill--warn" style={{ marginLeft: 8 }}>
                    Legal hold
                  </span>}
                </div>
              </div>
              <p className="small muted" style={{ margin: 0 }}>{data.retention.note}</p>
            </div>

            {can('envelopes:send') && (
              <div className="card">
                <h2 className="card__title">Actions</h2>
                <div className="btn-row btn-row--stack">
                  {active && (
                    <>
                      <button className="btn" disabled={busy}
                              onClick={() => void act('/remind', {}, 'Reminders sent.')}>
                        Remind everyone
                      </button>
                      <button className="btn btn--danger" onClick={() => setDialog('void')}>
                        Withdraw
                      </button>
                    </>
                  )}
                  {data.status !== 'completed' && !data.replaced_by_envelope_id && (
                    <button className="btn" onClick={() => setDialog('replace')}>
                      Create replacement
                    </button>
                  )}
                  {can('retention:manage') && !data.retention.on_hold && (
                    <button className="btn" onClick={() => setDialog('hold')}>
                      Place legal hold
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'Document' && (
          <div className="stack">
            <AssuranceNotice profile={data.assurance_profile} />
            <div className="btn-row btn-row--stack">
              {data.final_artifact_id && (
                <button className="btn btn--primary"
                        onClick={() => void downloadFile(`${docBase}/final`, 'final.pdf')}>
                  Download signed PDF
                </button>
              )}
              {data.completion_report_id && (
                <button className="btn"
                        onClick={() => void downloadFile(`${docBase}/report`, 'completion-report.pdf')}>
                  Completion report
                </button>
              )}
              <button className="btn"
                      onClick={() => void downloadFile(`${docBase}/original`, 'original.pdf')}>
                Original upload
              </button>
            </div>
            <PdfView
              url={data.final_artifact_id ? `${docBase}/final` : `${docBase}/rendition`}
              pageSizes={data.final_artifact_id ? data.document.page_sizes : data.document.page_sizes}
              renderOverlay={data.final_artifact_id ? undefined : (pageIndex, size) => (
                <>
                  {data.fields.filter((f: any) => f.page === pageIndex).map((field: any) => (
                    <div key={field.id}
                         className={`fieldbox${field.value ? ' fieldbox--done' : ''}`}
                         style={boxStyle(field, size)}>
                      <span className="fieldbox__value">{field.value || field.label}</span>
                    </div>
                  ))}
                </>
              )}
            />
          </div>
        )}

        {tab === 'Audit trail' && (
          !events ? <Loading /> : (
            <div className="stack">
              <Banner tone={events.integrity.passed ? 'ok' : 'danger'}
                      title={events.integrity.passed
                        ? 'Event record verified'
                        : 'Event record failed verification'}>
                {events.integrity.event_count} events, hash-chained and anchored in{' '}
                {events.integrity.checkpoints.length} signed checkpoint
                {events.integrity.checkpoints.length === 1 ? '' : 's'}.
                {!events.integrity.passed && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {events.integrity.findings.map((f: any, i: number) => (
                      <li key={i}>{f.message}</li>
                    ))}
                  </ul>
                )}
              </Banner>
              <div className="card card--pad-0">
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>#</th><th>Event</th><th>Actor</th><th>When</th>
                        <th>Outcome</th><th>Revision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.events.map((event: any) => (
                        <tr key={event.id}>
                          <td className="muted">{event.sequence}</td>
                          <td style={{ fontWeight: 540 }}>
                            {event.event_type.replace('envelope.', '').replace(/_/g, ' ')}
                          </td>
                          <td>
                            {event.actor_label || event.actor_type}
                            <div className="muted small">{event.actor_type}</div>
                          </td>
                          <td className="nowrap">{formatDateTime(event.occurred_at)}</td>
                          <td>{event.outcome || '—'}</td>
                          <td><span className="digest">
                            {shortDigest(event.document_digest || event.document_revision, 10)}
                          </span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <p className="small muted">
                IP addresses and user agents are recorded as supplemental attribution
                signals only. Automated link opens — email scanners, for example — are
                recorded as unverified automated access, not recipient engagement.
              </p>
            </div>
          )
        )}

        {tab === 'Evidence' && (
          !verification ? <Loading /> : (
            <div className="stack">
              <AssuranceNotice profile={data.assurance_profile} />

              <div className="grid grid--two">
                <div className="card stack-s">
                  <h3 className="card__title">Evidence integrity</h3>
                  <div className="kv">
                    <div className="kv__k">Manifest signature</div>
                    <div className="kv__v">
                      <span className={`pill pill--${verification.evidence.manifest.signature_valid ? 'ok' : 'danger'}`}>
                        {verification.evidence.manifest.signature_valid ? 'Valid' : 'Invalid'}
                      </span>
                    </div>
                    <div className="kv__k">Event chain</div>
                    <div className="kv__v">
                      <span className={`pill pill--${verification.evidence.event_chain.passed ? 'ok' : 'danger'}`}>
                        {verification.evidence.event_chain.passed ? 'Verified' : 'Failed'}
                      </span>
                    </div>
                    <div className="kv__k">Evidence key</div>
                    <div className="kv__v kv__v--mono">
                      {verification.evidence.evidence_key.key_id}
                    </div>
                  </div>
                </div>

                <div className="card stack-s">
                  <h3 className="card__title">External trust</h3>
                  <div className="kv">
                    <div className="kv__k">PDF seal</div>
                    <div className="kv__v">
                      <span className="pill">{verification.external_trust.pdf_seal.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="kv__k">Certificate trust</div>
                    <div className="kv__v">
                      <span className="pill">{verification.external_trust.certificate_trust.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="kv__k">Independent timestamp</div>
                    <div className="kv__v">
                      <span className="pill">
                        {verification.external_trust.independent_timestamp.replace(/_/g, ' ')}
                      </span>
                    </div>
                  </div>
                  <p className="small muted" style={{ margin: 0 }}>
                    {verification.external_trust.explanation}
                  </p>
                </div>
              </div>

              <div className="card card--pad-0">
                <div style={{ padding: '16px 18px 0' }}>
                  <h3 className="card__title">Revision chain</h3>
                  <p className="card__sub">
                    Every stored revision with its exact-byte digest. A different
                    digest after an authorized archival revision is expected.
                  </p>
                </div>
                <div className="table-wrap" style={{ marginTop: 12 }}>
                  <table className="table">
                    <thead>
                      <tr><th>Revision</th><th>SHA-256</th><th>Bytes</th><th>Created</th></tr>
                    </thead>
                    <tbody>
                      {verification.revision_chain.map((revision: any) => (
                        <tr key={revision.artifact_id}>
                          <td style={{ fontWeight: 540 }}>
                            {revision.kind.replace(/_/g, ' ')}
                          </td>
                          <td><span className="digest">{revision.digest}</span></td>
                          <td className="nowrap">{revision.byte_length.toLocaleString()}</td>
                          <td className="nowrap">{formatDateTime(revision.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {can('evidence:export') && (
                <div className="card stack-s">
                  <h3 className="card__title">Evidence export</h3>
                  <p className="small muted" style={{ margin: 0 }}>
                    A ZIP containing the original upload, the frozen signing
                    rendition, the final PDF, the completion report, consent and
                    authentication records, the ordered event snapshot, the signed
                    manifest and offline verification instructions. It verifies
                    without a live account and stays available after cancellation.
                  </p>
                  <button className="btn btn--primary"
                          onClick={() => void downloadFile(
                            `/organizations/${orgId}/envelopes/${envelopeId}/evidence`,
                            'evidence.zip')}>
                    Download evidence package
                  </button>
                </div>
              )}
            </div>
          )
        )}
      </div>

      {dialog === 'void' && (
        <Modal title="Withdraw this agreement" onClose={() => setDialog(null)}
               actions={<>
                 <button className="btn" onClick={() => setDialog(null)}>Cancel</button>
                 <button className="btn btn--danger" disabled={busy || reason.trim().length < 3}
                         onClick={() => void act('/void', { reason }, 'Agreement withdrawn.')}>
                   Withdraw
                 </button>
               </>}>
          <p className="small muted">
            Existing signing links stop working and recipients are notified. This
            does not restore the signature request to your allowance, and recorded
            signer actions are preserved in the evidence.
          </p>
          <Field label="Reason" id="void-reason" required>
            <textarea id="void-reason" className="textarea" value={reason}
                      onChange={(event) => setReason(event.target.value)} />
          </Field>
        </Modal>
      )}

      {dialog === 'replace' && (
        <Modal title="Create a replacement agreement" onClose={() => setDialog(null)}
               actions={<>
                 <button className="btn" onClick={() => setDialog(null)}>Cancel</button>
                 <button className="btn btn--primary" disabled={busy || reason.trim().length < 3}
                         onClick={() => void act('/replace', { reason },
                                                 'Replacement draft created.')}>
                   Create replacement
                 </button>
               </>}>
          <p className="small muted">
            Terms cannot be changed after sending. A replacement copies this
            agreement into a new draft, withdraws the original, and consumes a new
            signature request when it is sent.
          </p>
          <Field label="What is changing?" id="replace-reason" required>
            <textarea id="replace-reason" className="textarea" value={reason}
                      onChange={(event) => setReason(event.target.value)} />
          </Field>
        </Modal>
      )}

      {dialog === 'hold' && (
        <Modal title="Place a legal hold" onClose={() => setDialog(null)}
               actions={<>
                 <button className="btn" onClick={() => setDialog(null)}>Cancel</button>
                 <button className="btn btn--primary" disabled={busy || authority.trim().length < 3}
                         onClick={() => void act('/holds',
                           { scope: 'envelope', authority, note: reason },
                           'Legal hold placed.')}>
                   Place hold
                 </button>
               </>}>
          <p className="small muted">
            A hold suspends deletion of this agreement and its evidence beyond the
            normal retention deadline. The authority and scope are recorded.
          </p>
          <Field label="Authority" id="hold-authority" required
                 hint="Who required the hold, and under what matter or reference.">
            <input id="hold-authority" className="input" value={authority}
                   onChange={(event) => setAuthority(event.target.value)} />
          </Field>
          <Field label="Note" id="hold-note">
            <textarea id="hold-note" className="textarea" value={reason}
                      onChange={(event) => setReason(event.target.value)} />
          </Field>
        </Modal>
      )}
    </AppFrame>
  );
}
